import { InfoOutlined, ManageAccounts, Password } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import {
  CircularProgress,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import {
  QueryFilters,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { AgtStaffPermissions, MyStaffData } from '../../types';
import { captureError } from '../../utils/capture-error';
import { validatePassword } from '../../utils/validation';
import { HtmlTooltip } from './staff-add';

export function StaffRowManage(props: {
  row: MyStaffData;
  queryKey: QueryFilters['queryKey'];
}) {
  // Props
  const { row } = props;
  // State
  const [open, setOpen] = React.useState(false);
  const [resetPass, setResetPass] = React.useState(false);
  const [data, setData] = React.useState({
    FName: '',
    LName: '',
    Active: false,
    MyStaff_Comms_Allow: false,
    MyStaff_Debt_Allow: false,
    MyStaff_Profile_Allow: false,
    MyStaff_Wallet_Allow: false,
    Password: '',
    ConfirmPassword: '',
  });

  React.useEffect(() => {
    setData({
      FName: row.FName,
      LName: row.LName,
      Active: row.Active,
      MyStaff_Comms_Allow: row.Permissions.includes(
        AgtStaffPermissions['Commissions'],
      ),
      MyStaff_Debt_Allow: row.Permissions.includes(
        AgtStaffPermissions['Debts'],
      ),
      MyStaff_Profile_Allow: row.Permissions.includes(
        AgtStaffPermissions['Profile'],
      ),
      MyStaff_Wallet_Allow: row.Permissions.includes(
        AgtStaffPermissions['Wallet'],
      ),
      Password: '',
      ConfirmPassword: '',
    });
  }, [row, open]);

  const queryClient = useQueryClient();
  //Mutation - Update Staff Account
  const mutationUpdate = useMutation({
    mutationFn: async () => {
      await API.post('ContractingAPI', '/my-staff/update', {
        body: {
          Username: row.Username,
          FName: data.FName,
          LName: data.LName,
          Active: Boolean(data.Active),
        },
      });
    },
    onSuccess: () => queryClient.invalidateQueries(props.queryKey),
    onError: (error) => captureError({ data: { error } }),
  });

  // Mutation - Set Permissions
  const mutationPerms = useMutation({
    mutationFn: async (perm: { type: string; active: boolean }) => {
      await API.post('ContractingAPI', '/my-staff/set-permission', {
        body: {
          Username: row.Username,
          Permission: perm.type,
          Active: perm.active,
        },
      });
    },
    onSuccess: async (d, variables) => {
      setData({
        ...data,
        [variables.type]: variables.active,
      });
      queryClient.invalidateQueries(props.queryKey);
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Mutation - Reset Password
  const mutationPassword = useMutation({
    mutationFn: async () => {
      await API.post('ContractingAPI', '/my-staff/password-reset', {
        body: { Username: row.Username, TempPassword: data.Password },
      });
    },
    onSuccess: async () => {
      setResetPass(false);
    },
    onError: (error) => captureError({ data: { error }, removeData: true }),
  });

  const handleToggle = () => {
    setResetPass(false);
    mutationUpdate.reset();
    mutationPerms.reset();
    mutationPassword.reset();
    setOpen((open) => !open);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutationPerms.mutate({ type: e.target.name, active: e.target.checked });
  };

  const mismatchPass = data.Password !== data.ConfirmPassword;

  return (
    <React.Fragment>
      <IconButton
        size="small"
        sx={{ backgroundColor: '#fff' }}
        onClick={handleToggle}
      >
        <ManageAccounts fontSize="small" />
      </IconButton>
      <Drawer
        container={window.document.body}
        variant="temporary"
        anchor="right"
        open={open}
        onClose={handleToggle}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: '100vw', sm: '80vw', md: '60vw', lg: '40vw' },
            height: '100dvh',
          },
        }}
      >
        <Box
          sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}
        >
          <Toolbar sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              aria-label="close"
              sx={{ mr: 2 }}
              onClick={handleToggle}
            >
              <Close />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Manage Account
            </Typography>
            <Box flex={1} />
            <Box>
              <Tooltip title="Reset Password" placement="left" arrow>
                <IconButton onClick={() => setResetPass(!resetPass)}>
                  <Password />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>

          <Divider />

          {mutationPassword.isError ? (
            <Alert severity="error" onClose={() => mutationPassword.reset()}>
              <AlertTitle>Error</AlertTitle>
              {
                // @ts-ignore
                mutationPassword?.error?.response?.data?.error ||
                  'Something went wrong. Please try again.'
              }
            </Alert>
          ) : null}

          {mutationPassword.isSuccess ? (
            <Alert severity="success" onClose={() => mutationPassword.reset()}>
              Password reset successful!
            </Alert>
          ) : null}

          {resetPass ? (
            <Alert
              severity="warning"
              action={
                <Stack spacing={1} direction="row" alignItems="center">
                  <Button
                    //disabled={isLoading}
                    size="small"
                    color="inherit"
                    variant="outlined"
                    onClick={() => setResetPass(false)}
                  >
                    Cancel
                  </Button>

                  <Button
                    disabled={
                      !data.Password ||
                      mismatchPass ||
                      mutationPassword.isLoading ||
                      validatePassword({ password: data.Password })
                    }
                    size="small"
                    color="error"
                    variant="contained"
                    disableElevation
                    onClick={() => mutationPassword.mutate()}
                  >
                    {mutationPassword.isLoading
                      ? 'Resetting...'
                      : 'Reset Password'}
                  </Button>
                </Stack>
              }
            >
              <AlertTitle>Create Temporary Password</AlertTitle>
              <Stack spacing={2}>
                <FormControl fullWidth variant="outlined" required size="small">
                  <InputLabel>Temporary Password</InputLabel>
                  <OutlinedInput
                    label="Temporary Password"
                    name="Password"
                    onChange={handleChange}
                    value={data.Password}
                    style={{ backgroundColor: '#fff' }}
                    error={validatePassword({ password: data.Password })}
                    autoComplete="off"
                    endAdornment={
                      <HtmlTooltip
                        arrow
                        placement="top"
                        title={
                          <div style={{ padding: 5 }}>
                            Password <strong>MUST</strong> include:
                            <ul style={{ marginTop: 0 }}>
                              <li> 14+ characters</li>
                              <li> uppercase letter</li>
                              <li>lowercase letter</li>
                              <li>number</li>
                              <li>special character</li>
                            </ul>
                            Password must <strong>NOT</strong> contain
                            <ul style={{ marginTop: 0 }}>
                              <li>your name</li>
                              <li>your agent number</li>
                            </ul>
                          </div>
                        }
                      >
                        <InfoOutlined fontSize="small" color="info" />
                      </HtmlTooltip>
                    }
                  />
                  {validatePassword({ password: data.Password }) ? (
                    <Typography
                      variant="caption"
                      style={{
                        color: '#D32F2F',
                        paddingLeft: 10,
                      }}
                    >
                      Invalid Password
                    </Typography>
                  ) : null}
                </FormControl>

                <FormControl fullWidth variant="outlined" required size="small">
                  <InputLabel>Confirm Password</InputLabel>
                  <OutlinedInput
                    label="Confirm Password"
                    name="ConfirmPassword"
                    onChange={handleChange}
                    value={data.ConfirmPassword}
                    style={{ backgroundColor: '#fff' }}
                    autoComplete="off"
                    error={
                      validatePassword({
                        password: data.ConfirmPassword,
                      }) || mismatchPass
                    }
                  />
                  {mismatchPass ? (
                    <Typography
                      variant="caption"
                      style={{
                        color: '#D32F2F',
                        paddingLeft: 10,
                      }}
                    >
                      Passwords do not match
                    </Typography>
                  ) : null}
                </FormControl>
              </Stack>
            </Alert>
          ) : null}

          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={2} sx={{ p: 2 }}>
              <TextField
                fullWidth
                disabled
                label="Username"
                name="Username"
                value={row.Username}
                sx={{
                  backgroundColor: '#fff',
                  '& input.Mui-disabled': {
                    WebkitTextFillColor: '#b26500',
                  },
                }}
              />

              <TextField
                fullWidth
                disabled
                label="Email"
                name="Email"
                type="email"
                value={row.Email}
                sx={{
                  backgroundColor: '#fff',
                }}
              />

              <TextField
                fullWidth
                label="First Name"
                name="FName"
                onChange={handleChange}
                required
                value={data.FName}
                sx={{
                  backgroundColor: '#fff',
                }}
              />

              <TextField
                fullWidth
                label="Last Name"
                name="LName"
                onChange={handleChange}
                required
                value={data.LName}
                sx={{
                  backgroundColor: '#fff',
                }}
              />
              <FormControl>
                <InputLabel>Status</InputLabel>

                <Select
                  label="Status"
                  value={data.Active}
                  onChange={(e) => {
                    setData({
                      ...data,
                      Active: e.target.value === 'true' ? true : false,
                    });
                  }}
                  sx={{ color: data.Active === true ? 'green' : 'inherit' }}
                >
                  <MenuItem value={'true'}>Active</MenuItem>
                  <MenuItem value={'false'}>Inactive</MenuItem>
                </Select>
              </FormControl>
              <Box>
                {mutationUpdate.isSuccess ? (
                  <Alert
                    severity="success"
                    onClose={() => mutationUpdate.reset()}
                  >
                    Account has been updated!
                  </Alert>
                ) : null}

                {mutationUpdate.isError ? (
                  <Alert
                    severity="error"
                    onClose={() => mutationUpdate.reset()}
                  >
                    <AlertTitle>Error</AlertTitle>
                    {
                      // @ts-ignore
                      mutation.error?.response?.data?.error ||
                        'An error has occurred while trying to update staff account'
                    }
                  </Alert>
                ) : null}

                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  color="info"
                  disabled={
                    mutationUpdate.isError ||
                    (row.FName === data.FName &&
                      row.LName === data.LName &&
                      row.Active === data.Active)
                  }
                  onClick={() => mutationUpdate.mutate()}
                >
                  {mutationUpdate.isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>

              {mutationPerms.isSuccess && (
                <Alert severity="success" onClose={() => mutationPerms.reset()}>
                  <AlertTitle>Permissions successfully updated!</AlertTitle>
                  Users will need to log out and then log back in after any
                  permission changes
                </Alert>
              )}

              <Stack direction="row">
                <FormControl
                  component="fieldset"
                  disabled={mutationPerms.isLoading}
                  sx={{ pl: 2 }}
                >
                  <FormLabel
                    component="legend"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    Assign Permissions{' '}
                    {!mutationPerms.isLoading ? (
                      <HtmlTooltip
                        style={{
                          marginLeft: 5,
                        }}
                        arrow
                        placement="top"
                        title={
                          <div style={{ padding: 5 }}>
                            Commissions:
                            <ul style={{ marginTop: 0 }}>
                              <li>View Commissions (ARC)</li>
                              <li>View My $ Chart (ARC)</li>
                            </ul>
                            Debts:
                            <ul style={{ marginTop: 0 }}>
                              <li>View Debts (ARC)</li>
                            </ul>
                            Profile:
                            <ul style={{ marginTop: 0 }}>
                              <li>View/Edit Profile</li>
                            </ul>
                            Wallet:
                            <ul style={{ marginTop: 0 }}>
                              <li>View/Edit My Wallet & Payment Info </li>
                              <li>Make Debt Payments (ARC) </li>
                              <li>Purchase Event Tickets </li>
                              <li>Purchase Leads & Create Subscriptions</li>
                            </ul>
                          </div>
                        }
                      >
                        <InfoOutlined fontSize="small" color="info" />
                      </HtmlTooltip>
                    ) : (
                      <CircularProgress color="info" size={18} sx={{ ml: 1 }} />
                    )}
                  </FormLabel>
                  <FormGroup sx={{ justifyContent: 'space-between', pl: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="MyStaff_Comms_Allow"
                          checked={data.MyStaff_Comms_Allow}
                          onChange={handleCheck}
                        />
                      }
                      label="Commissions"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          name="MyStaff_Debt_Allow"
                          checked={data.MyStaff_Debt_Allow}
                          onChange={handleCheck}
                        />
                      }
                      label="Debts"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="MyStaff_Profile_Allow"
                          checked={data.MyStaff_Profile_Allow}
                          onChange={handleCheck}
                        />
                      }
                      label="Profile"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="MyStaff_Wallet_Allow"
                          checked={data.MyStaff_Wallet_Allow}
                          onChange={handleCheck}
                        />
                      }
                      label="Wallet"
                    />
                  </FormGroup>
                </FormControl>{' '}
                <Box flex={1} />
              </Stack>
            </Stack>
          </Box>

          <Divider />
        </Box>
      </Drawer>
    </React.Fragment>
  );
}
